import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  MapPin, Handshake, X as XIcon, ExternalLink, Plus,
  Loader2, Send, Trash2, Radar as RadarIcon, Bot, Sparkles,
  FileText, Eye, Mic, HelpCircle, Check,
} from 'lucide-react';
import { useWatchApi, FeedItem, Radar, AutoRadar, AgentJobCard, MarketPulse, Watcher, VisibleJob } from '../services/watchApi';
import SafeMarkdown from '../components/SafeMarkdown';
import LiveRadarStrip from '../components/Watch/LiveRadarStrip';
import RadarAnimation from '../components/Watch/RadarAnimation';
import { useVoiceDictation } from '../hooks/useVoiceDictation';
import { useAuth } from '../hooks/useAuth';
import { useAuthModal } from '../hooks/useAuthModal';
import AuthModal from '../components/Auth/AuthModal';
import RateLimitNotification from '../components/RateLimitNotification';
import type { RateLimitInfo } from '../utils/errorHandler';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/**
 * Job-Radar KI (AI Mode) — Phase 1 "chat skin".
 *
 * Reuses the existing consumer Watch APIs (radars, feed, interested, dismiss,
 * load-more, create/delete watcher). Renders each radar (+ the CV-based "For
 * you" auto radar) as a chat THREAD: Vee posts matching jobs as messages with
 * compact job cards. Conversational agent + radars share the same composer.
 */

const EARLY_CAREER = new Set(['ausbildung', 'dual_study', 'werkstudent', 'praktikum']);
const FEED_PREVIEW = 3;
// Fetch the full surfaced backlog, not just the latest slice. "Mehr aus dem
// Radar" surfaces new jobs with the newest surfaced_at; a tight limit (was 5)
// meant the fresh batch filled the whole window and the original recommendations
// fell off the result entirely — so the top feed looked wiped after loading
// more. The collapsed view still previews only FEED_PREVIEW; this just keeps the
// already-surfaced cards in the payload so they survive load-more + polls.
const FEED_API_LIMIT = 60;
/** Synthetic chat rows — not persisted server-side; re-built from live feed. */
const FEED_SEED_ID = 'feed-seed';
const FEED_BURST_ID = 'feed-burst';
/** Each "Mehr aus dem Radar" pull renders as its OWN burst bubble (never merged
 *  with the previous batch), so the ids are `feed-burst-<batchIndex>` / `-new`. */
const FEED_BURST_PREFIX = 'feed-burst-';
/** Per-day feed card bubbles get a stable `feed-day-<dayKey>` id so expand
 *  state survives polls and they're treated as rebuildable feed-meta rows. */
const FEED_DAY_PREFIX = 'feed-day-';
/** Per-browser radar tab order (drag-to-reorder); persisted in localStorage. */
const RADAR_ORDER_KEY = 'watch_radar_order';
/** Per-browser "Mehr aus dem Radar" pulled-job ids, persisted per thread so the
 *  separate "weitere Jobs" burst survives a page refresh — otherwise those pulls
 *  collapse back into the daily "neue Treffer" group and inflate its count. */
const PULLED_MORE_KEY = 'watch_pulled_more';

function loadPulledMore(): Map<ThreadId, Set<string>[]> {
  try {
    const raw = JSON.parse(localStorage.getItem(PULLED_MORE_KEY) || '{}');
    const map = new Map<ThreadId, Set<string>[]>();
    for (const [k, v] of Object.entries(raw)) {
      if (!Array.isArray(v)) continue;
      // New format: array of batches (array of id-arrays). Back-compat: a flat
      // array of ids is read as one single batch.
      if (v.length > 0 && Array.isArray(v[0])) {
        map.set(k, (v as string[][]).map(b => new Set(b)).filter(s => s.size));
      } else if (v.length > 0) {
        map.set(k, [new Set(v as string[])]);
      }
    }
    return map;
  } catch {
    return new Map();
  }
}

function savePulledMore(map: Map<ThreadId, Set<string>[]>): void {
  try {
    const obj: Record<string, string[][]> = {};
    map.forEach((batches, k) => {
      const arr = batches.map(s => Array.from(s)).filter(b => b.length);
      if (arr.length) obj[k] = arr;
    });
    localStorage.setItem(PULLED_MORE_KEY, JSON.stringify(obj));
  } catch { /* localStorage full / unavailable — non-critical */ }
}
/** Cevee wordmark gradient (Header logo) — Vee avatar background. */
const CEVEE_AVATAR_GRADIENT =
  'radial-gradient(circle at 52% center, #fde047 0%, #f9a8d4 25%, #db2777 55%, #881337 100%)';
/** Logo text gradient — send button & CTAs. */
const CEVEE_GRADIENT = 'bg-gradient-to-r from-yellow-400 via-pink-500 to-rose-800';
const CEVEE_BTN_STYLE = {
  backgroundImage: 'linear-gradient(to right, #facc15 0%, #ec4899 52%, #9f1239 100%)',
};

function collectJobIds(messages: ChatMsg[]): Set<string> {
  const ids = new Set<string>();
  for (const m of messages) {
    for (const j of m.jobs || []) {
      if (j.job_id) ids.add(j.job_id);
    }
    for (const f of m.feedItems || []) {
      if (f.job_id) ids.add(f.job_id);
    }
  }
  return ids;
}

function visibleJobLocation(loc: string | null, country: string | null): string {
  if (loc && loc.trim()) return loc.trim();
  if ((country || '').toLowerCase() === 'remote') return 'Remote';
  return '';
}

/** Snapshot the jobs the user actually SEES, in exact top→bottom render order and
 *  grouped into the same batches (one per Vee bubble). The collapsed preview only
 *  shows FEED_PREVIEW cards, so mirror that slice — the agent must resolve
 *  "der N. von oben" against what's on screen, not the raw backlog. */
function collectVisibleJobBatches(
  messages: ChatMsg[],
  expanded: Set<string>,
): VisibleJob[][] {
  const batches: VisibleJob[][] = [];
  for (const m of messages) {
    if (m.role !== 'vee') continue;
    const feed = m.feedItems || [];
    const jobs = m.jobs || [];
    const isExpanded = expanded.has(m.id);
    const shownFeed = isExpanded ? feed : feed.slice(0, FEED_PREVIEW);
    const remaining = Math.max(0, FEED_PREVIEW - shownFeed.length);
    const shownJobs = isExpanded ? jobs : jobs.slice(0, remaining);
    const batch: VisibleJob[] = [];
    for (const f of shownFeed) {
      if (!f.job_id) continue;
      batch.push({
        job_id: f.job_id,
        company_id: f.company_id || '',
        title: f.title || '',
        company: f.company_name || '',
        location: visibleJobLocation(f.location, f.country),
      });
    }
    for (const j of shownJobs) {
      if (!j.job_id) continue;
      batch.push({
        job_id: j.job_id,
        company_id: j.company_id || '',
        title: j.title || '',
        company: j.company_name || '',
        location: visibleJobLocation(j.location, j.country),
      });
    }
    if (batch.length) batches.push(batch);
  }
  return batches;
}

function dedupeAgentJobs(jobs: AgentJobCard[], seen: Set<string>): AgentJobCard[] {
  const out: AgentJobCard[] = [];
  for (const j of jobs) {
    if (!j.job_id || seen.has(j.job_id)) continue;
    seen.add(j.job_id);
    out.push(j);
  }
  return out;
}

type ThreadId = string; // 'auto' | 'interested' | <watcherId>

interface ThreadSnapshot {
  items: FeedItem[];
  chatLog: ChatMsg[];
  expandedGroups: Set<string>;
}

interface ChatMsg {
  id: string;
  role: 'user' | 'vee';
  text?: string;
  jobs?: AgentJobCard[];
  feedItems?: FeedItem[];
  createdAt: number;
}

let _msgSeq = 0;
const nextMsgId = () => `m${Date.now()}_${_msgSeq++}`;

/** API stores Unix seconds; UI uses milliseconds. */
function epochMs(ts: number): number {
  if (!ts) return Date.now();
  return ts < 1e12 ? ts * 1000 : ts;
}

function radarLabel(r: Radar): string {
  const q = (r.query || '').trim();
  const f = r.filters || {};
  const loc = (f.location as string | undefined)?.trim();
  const ets = (f.employment_type || []) as string[];
  if (r.label?.trim()) return r.label.trim();
  const parts = [q].filter(Boolean);
  if (loc) parts.push(loc);
  else if (ets.length) parts.push(ets[0]);
  return parts.join(' · ') || q;
}

function watcherToRadar(w: Watcher): Radar {
  return {
    id: w.id,
    type: 'role',
    label: w.label,
    query: w.query,
    filters: w.filters || {},
    is_active: w.is_active,
    last_seen_at: null,
    created_at: w.created_at,
    total: 0,
    unread: 0,
    last_item_at: null,
  };
}

function sameCalendarDay(a: number, b: number): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear()
    && da.getMonth() === db.getMonth()
    && da.getDate() === db.getDate()
  );
}

function formatDayLabel(
  ts: number,
  locale: string,
  t: (key: string) => string,
): string {
  const d = new Date(epochMs(ts));
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startD = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((startToday - startD) / 86400000);
  if (diff === 0) return t('chat.today');
  if (diff === 1) return t('chat.yesterday');
  return d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
}

function isFeedMetaMsg(m: ChatMsg): boolean {
  return m.id === FEED_SEED_ID
    || m.id === FEED_BURST_ID
    || m.id.startsWith(FEED_BURST_PREFIX)
    || m.id.startsWith(FEED_DAY_PREFIX);
}

/** Local calendar-day key (YYYY-MM-DD) for grouping feed cards by surfaced day. */
function dayKey(tsMs: number): string {
  const d = new Date(tsMs);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** When a job surfaced, in ms. Falls back to "now" for the rare null row so it
 *  groups into today rather than 1970. */
function feedItemTs(i: FeedItem): number {
  return i.surfaced_at ? epochMs(i.surfaced_at) : Date.now();
}

function isDuplicateForYouGreeting(text: string, greeting: string | null): boolean {
  if (!greeting) return false;
  const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();
  const t = norm(text);
  const g = norm(greeting);
  if (t === g) return true;
  const opener = 'hey! ich halte für dich ausschau';
  const openerEn = "hey! i'm keeping an eye out for you";
  return (t.startsWith(opener) && g.startsWith(opener))
    || (t.startsWith(openerEn) && g.startsWith(openerEn));
}

function profileHasJobPrefs(rolePreferences: string | null, preferredLocations: string | null): boolean {
  return !!(rolePreferences?.trim() || preferredLocations?.trim());
}

/** Merge live feed job cards into ONE chronological timeline with persisted chat
 *  text. Feed cards are grouped into one Vee bubble per surfaced day, so the chat
 *  reads oldest→newest with correct day dividers and the newest matches always
 *  sit at the bottom.
 *
 *  This replaces the old "single seed pinned to the top, stamped today" model,
 *  which produced an impossible `Heute → Gestern → Heute` order and merged
 *  multiple days of cards into one inflated "X neue Treffer" line.
 *
 *  Two batches still break out of the day grouping so they land where the user
 *  expects, at the bottom:
 *    1) jobs explicitly pulled via "more from radar" (dedicated copy);
 *    2) genuinely-new matches that arrive *while the user is chatting* (after the
 *       last chat message) — a fresh "X neue Treffer" burst, so already-seen
 *       cards don't jump out of their day group when a poll brings one in. */
function mergeFeedIntoChatLog(
  history: ChatMsg[],
  feedItems: FeedItem[],
  opts: {
    selected: ThreadId;
    greeting: string | null;
    threadLabel: string;
    newSinceSeenItems: FeedItem[];
    pulledMoreBatches: Set<string>[];
    hasJobPrefs: boolean;
    autoEnabled: boolean;
    t: (key: string, opts?: Record<string, unknown>) => string;
  },
): ChatMsg[] {
  const {
    selected, greeting, threadLabel,
    newSinceSeenItems, pulledMoreBatches, hasJobPrefs, autoEnabled, t,
  } = opts;
  const conversation = history.filter(m => !isFeedMetaMsg(m));
  const chatBody = greeting
    ? conversation.filter(m => !(m.role === 'vee' && isDuplicateForYouGreeting(m.text || '', greeting)))
    : conversation;

  if (selected === 'interested') {
    if (feedItems.length === 0) return chatBody;
    return [{
      id: FEED_SEED_ID,
      role: 'vee',
      text: t('threads.interested'),
      feedItems: feedItems,
      createdAt: Date.now() - 3000,
    }, ...chatBody];
  }

  const timeline: ChatMsg[] = [];

  // --- Bottom batches (excluded from day groups) -----------------------------
  // Each "Mehr aus dem Radar" pull is its OWN bubble (never merged with the
  // previous 5): one burst per recorded batch, in click order (newest at bottom).
  const burstIds = new Set<string>();
  pulledMoreBatches.forEach((batch, idx) => {
    if (!batch || batch.size === 0) return;
    const batchItems = feedItems.filter(
      i => i.job_id && batch.has(i.job_id) && !burstIds.has(i.id),
    );
    if (batchItems.length === 0) return;
    batchItems.forEach(i => burstIds.add(i.id));
    // Best fit on top within this pulled batch.
    batchItems.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    const burstTs = batchItems.reduce((max, i) => Math.max(max, feedItemTs(i)), 0);
    timeline.push({
      id: `${FEED_BURST_PREFIX}${idx}`,
      role: 'vee',
      text: t('feedPrompt.moreJobsResult', { count: batchItems.length }),
      feedItems: batchItems,
      // +idx keeps click order stable when successive pulls tie on surfaced_at.
      createdAt: (burstTs || Date.now()) + idx,
    });
  });

  // Genuinely-new matches that arrive WHILE the user is chatting (a poll) get
  // their own "neue Treffer" burst so already-seen cards don't jump day groups.
  const lastChatTs = chatBody.reduce((max, m) => Math.max(max, m.createdAt || 0), 0);
  const newMidChat = chatBody.length > 0
    ? newSinceSeenItems.filter(i => feedItemTs(i) > lastChatTs && !burstIds.has(i.id))
    : [];
  if (newMidChat.length > 0) {
    newMidChat.forEach(i => burstIds.add(i.id));
    const sorted = [...newMidChat].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    const burstTs = sorted.reduce((max, i) => Math.max(max, feedItemTs(i)), 0);
    timeline.push({
      id: `${FEED_BURST_PREFIX}new`,
      role: 'vee',
      text: selected === 'auto'
        ? t('vee.newMatchesForYou', { count: sorted.length })
        : t('vee.newMatches', { count: sorted.length, label: threadLabel }),
      feedItems: sorted,
      createdAt: burstTs || Date.now(),
    });
  }

  // --- One bubble per surfaced day -------------------------------------------
  const newIds = new Set(newSinceSeenItems.map(i => i.id));
  const todayKey = dayKey(Date.now());
  const groups = new Map<string, FeedItem[]>();
  feedItems.forEach((it) => {
    if (burstIds.has(it.id)) return;
    const key = dayKey(feedItemTs(it));
    const arr = groups.get(key);
    if (arr) arr.push(it); else groups.set(key, [it]);
  });
  Array.from(groups.entries()).forEach(([key, groupItems]) => {
    const dayTs = groupItems.reduce((max, i) => Math.max(max, feedItemTs(i)), 0);
    // Best fit on top WITHIN the day's batch (the feed is fetched newest-first for
    // natural history retention; the in-batch order is by match score).
    groupItems.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    // "neue Treffer" only for today's fresh batch; past days are plain history,
    // so the count is always per-day and never inflated across days.
    const isNewToday = key === todayKey && groupItems.some(i => newIds.has(i.id));
    const text = isNewToday
      ? (selected === 'auto'
          ? t('vee.newMatchesForYou', { count: groupItems.length })
          : t('vee.newMatches', { count: groupItems.length, label: threadLabel }))
      : (selected === 'auto'
          ? t('vee.matchesForYou', { count: groupItems.length })
          : t('vee.matchesRadar', { count: groupItems.length, label: threadLabel }));
    timeline.push({
      id: `${FEED_DAY_PREFIX}${key}`,
      role: 'vee',
      text,
      feedItems: groupItems,
      createdAt: dayTs || Date.now(),
    });
  });

  // Conversation rows keep their real timestamps and interleave chronologically.
  timeline.push(...chatBody);

  // Greeting (+ prefs nudge for "For you" without prefs, + "nothing new" when the
  // feed is empty) is a header bubble pinned just before the earliest row so it
  // always opens the thread. Skipped while "For you" is OFF (no CV / not enabled)
  // — there the EmptyThread enable-prompt is the only thing that should show, not
  // a "Hey, I'm scanning based on your CV" greeting for a radar that isn't running.
  const showGreeting = !(selected === 'auto' && !autoEnabled);
  const greetingLines = (showGreeting ? [greeting] : []).filter(Boolean) as string[];
  if (showGreeting && selected === 'auto' && !hasJobPrefs) greetingLines.push(t('vee.nudgePrefs'));
  if (showGreeting && feedItems.length === 0) greetingLines.push(t('vee.noNew'));
  if (greetingLines.length > 0) {
    const earliest = timeline.reduce(
      (min, m) => Math.min(min, m.createdAt || Infinity), Infinity);
    timeline.push({
      id: FEED_SEED_ID,
      role: 'vee',
      text: greetingLines.join('\n\n'),
      createdAt: Number.isFinite(earliest) ? earliest - 1000 : Date.now(),
    });
  }

  // Stable chronological order — greeting first, newest matches at the bottom.
  timeline.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  return timeline;
}

function feedMergeOpts(
  sel: ThreadId,
  rows: FeedItem[],
  auto: AutoRadar | null,
  radars: Radar[],
  threadLabel: string,
  hasJobPrefs: boolean,
  t: (key: string, opts?: Record<string, unknown>) => string,
  pulledMoreBatches: Set<string>[] = [],
) {
  const threadLastSeen =
    sel === 'auto' ? (auto?.last_seen_at || 0) :
    sel === 'interested' ? 0 :
    (radars.find(x => x.id === sel)?.last_seen_at || 0);
  const newSinceSeenItems =
    sel === 'interested'
      ? []
      : rows.filter(i => i.status === 'new' && (i.surfaced_at ?? 0) > threadLastSeen);
  const autoEnabled = !!auto?.is_active;
  const greeting =
    sel === 'auto'
      ? (hasJobPrefs ? t('vee.greetingForYouWithPrefs') : t('vee.greetingForYou'))
      : sel === 'interested' ? null :
    t('vee.greetingRadar', { label: threadLabel });
  return {
    selected: sel,
    greeting,
    threadLabel,
    autoEnabled,
    // Genuinely-new-since-last-visit rows. The feed fetches the full surfaced
    // backlog, so this (not a raw status='new' count) drives the per-day "neue
    // Treffer" highlight without inflating it with already-seen items.
    newSinceSeenItems,
    pulledMoreBatches,
    hasJobPrefs,
    t,
  };
}

function threadLabelFor(
  sel: ThreadId,
  auto: AutoRadar | null,
  radars: Radar[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (sel === 'auto') return t('threads.forYou');
  if (sel === 'interested') return t('threads.interested');
  const r = radars.find(x => x.id === sel);
  return r ? radarLabel(r) : t('threads.new');
}

interface JobRadarAIViewProps {
  /** Push the live total unread (auto + all role radars) up to the shell so the
   *  Radar tab badge tracks reads in real time — clearing a thread's unread here
   *  immediately decrements the tab badge, no focus-poll round-trip needed. */
  onUnseenChange?: (total: number) => void;
}

const JobRadarAIView: React.FC<JobRadarAIViewProps> = ({ onUnseenChange }) => {
  const api = useWatchApi();
  const { user, apiCall, isSignedIn, isLoading: authLoading } = useAuth();
  const { showAuthModal, setShowAuthModal } = useAuthModal();
  const { t, i18n } = useTranslation('jobRadarAi');
  const { t: tc } = useTranslation('common');
  const locale = i18n.language?.startsWith('de') ? 'de-DE' : 'en-US';

  const [radars, setRadars] = useState<Radar[]>([]);
  const [auto, setAuto] = useState<AutoRadar | null>(null);
  const [selected, setSelected] = useState<ThreadId>('auto');
  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedLoading, setFeedLoading] = useState(false);
  const [composer, setComposer] = useState('');
  const handleVoiceTranscribed = useCallback((text: string) => {
    setComposer(prev => (prev.trim() ? `${prev.trim()} ${text}` : text));
  }, []);
  const voice = useVoiceDictation(handleVoiceTranscribed, {
    maxSeconds: 120,
    onError: (code) => {
      if (code === 'audio_too_large') setError(t('composer.micError'));
    },
  });
  const [error, setError] = useState<string | null>(null);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const [veeNote, setVeeNote] = useState<string | null>(null);
  const [chatLog, setChatLog] = useState<ChatMsg[]>([]);
  const [sending, setSending] = useState(false);
  const [pulse, setPulse] = useState<MarketPulse | null>(null);
  const [createInput, setCreateInput] = useState('');
  const [creating, setCreating] = useState(false);
  // The create-radar bar is collapsible: once the user already has radars ("Für
  // dich" or custom), an always-open search field just adds clutter (esp. on
  // mobile). Default collapsed to a compact "+ Radar" trigger; auto-open once for
  // first-timers with no radars so it stays discoverable.
  const [createOpen, setCreateOpen] = useState(false);
  const createAutoOpened = useRef(false);
  const [enablingAuto, setEnablingAuto] = useState(false);
  // Per-bubble "show all" state (keyed by ChatMsg id). Each feed/result group
  // previews FEED_PREVIEW cards and expands independently — no global toggle.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = useCallback((id: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const [feedCapReached, setFeedCapReached] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchingSimilar, setSearchingSimilar] = useState(false);
  const [howOpen, setHowOpen] = useState(false);
  const [pendingRadarId, setPendingRadarId] = useState<string | null>(null);
  const [radarOrder, setRadarOrder] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(RADAR_ORDER_KEY) || '[]'); } catch { return []; }
  });
  const [dragId, setDragId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Profile signals for the "Intro me" visibility gate (mirrors WatchView).
  const [careerStatus, setCareerStatus] = useState<string | null>(null);
  const [hasCv, setHasCv] = useState<boolean | null>(null);
  const [hasJobPrefs, setHasJobPrefs] = useState(false);
  const [nudge, setNudge] = useState<null | 'cv' | 'status'>(null);
  // Why the CV nudge fired — so its copy matches the context: 'auto' (enabling
  // the CV-matched "For you" radar) vs 'intro' (introducing the user to a company).
  const [cvReason, setCvReason] = useState<'auto' | 'intro'>('intro');
  const [savingStatus, setSavingStatus] = useState(false);
  const [pendingIntroItem, setPendingIntroItem] = useState<FeedItem | null>(null);
  const didLoadProfile = useRef(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  /** Skip the next auto-scroll (card actions like Intro me should not jump the view). */
  const suppressScrollRef = useRef(false);

  // Fill exactly from the top of this view to the bottom of the viewport. The
  // chrome above (Header + Home2 tab bar) differs by breakpoint/device, so we
  // measure it instead of subtracting a magic number — otherwise the view ends
  // short and leaves a gap below the composer.
  const rootRef = useRef<HTMLDivElement>(null);
  const [viewHeight, setViewHeight] = useState<number | undefined>(undefined);
  const recomputeViewHeight = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const h = Math.round(vh - top);
    if (h > 200) setViewHeight(h);
  }, []);
  useLayoutEffect(() => {
    recomputeViewHeight();
    const vv = window.visualViewport;
    window.addEventListener('resize', recomputeViewHeight);
    window.addEventListener('orientationchange', recomputeViewHeight);
    vv?.addEventListener('resize', recomputeViewHeight);
    // iOS shifts the visual viewport (not just resizes) when the keyboard pins —
    // recompute on that too so the height tracks the keyboard precisely.
    vv?.addEventListener('scroll', recomputeViewHeight);
    return () => {
      window.removeEventListener('resize', recomputeViewHeight);
      window.removeEventListener('orientationchange', recomputeViewHeight);
      vv?.removeEventListener('resize', recomputeViewHeight);
      vv?.removeEventListener('scroll', recomputeViewHeight);
    };
  }, [recomputeViewHeight]);

  // Lock global page scroll while the Radar is mounted: the composer stays
  // pinned and only the chat message list scrolls. This also stops iOS from
  // scrolling the <body> to reveal the focused composer — the cause of the
  // first-focus jump where the keyboard briefly didn't sit under the input.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyOverscroll: body.style.overscrollBehavior,
    };
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.overscrollBehavior = prev.bodyOverscroll;
    };
  }, []);
  /** Snap to the very bottom once content lands after opening a thread. The feed
   *  loads async, so an immediate scroll-on-select fires before cards render;
   *  this latches until the first populated render so "Für dich" / a radar always
   *  opens at the newest matches. */
  const pendingScrollBottomRef = useRef(true);
  /** Instant tab switches — refresh feed/history in the background. */
  const threadCacheRef = useRef<Map<ThreadId, ThreadSnapshot>>(new Map());
  /** Job ids surfaced via an explicit "more from radar" pull, per thread. These
   *  render as a bottom "here are more" burst (chat-native) and are excluded from
   *  the top seed, so pulling more never re-grows the list at the top. */
  const pulledMoreIdsRef = useRef<Map<ThreadId, Set<string>[]>>(loadPulledMore());
  /** Ignore stale feed/history responses when the user switches tabs quickly. */
  const activeThreadLoadRef = useRef<ThreadId>('auto');
  const selectedRef = useRef<ThreadId>('auto');
  /** Animate each chat row once; tab remounts should not replay the whole thread. */
  const animatedMsgIdsRef = useRef<Set<string>>(new Set());
  const autoEnabled = !!auto?.is_active;

  const loadRadars = useCallback(async () => {
    try {
      const r = await api.getRadars();
      setRadars(r.radars || []);
      setAuto(r.auto || null);
      return r;
    } catch {
      return { radars: [] as Radar[], auto: null as AutoRadar | null };
    }
  }, [api]);

  // Optimistically clear a thread's unread badge after marking it seen. We do
  // NOT follow with a loadRadars() here: getRadars() re-runs the server-side
  // _promote_due, which surfaces today's drop with surfaced_at=now (AFTER the
  // seen stamp) and would instantly re-mark the radar unread — the root cause of
  // the "the +X only disappears the second time you open it" bug.
  const clearLocalUnread = useCallback((sel: ThreadId) => {
    if (sel === 'auto') {
      setAuto(prev => (prev ? { ...prev, unread: 0 } : prev));
    } else if (sel !== 'interested') {
      setRadars(prev => prev.map(r => (r.id === sel ? { ...r, unread: 0 } : r)));
    }
  }, []);

  // Live tab badge: report the true total unread (auto + role radars) to the
  // shell whenever it changes. Opening a thread clears its unread above, which
  // flows straight into the Radar tab badge — so reading here makes the badge
  // tick down everywhere in real time, instead of waiting for the next poll.
  useEffect(() => {
    if (!onUnseenChange) return;
    const total = (auto?.unread || 0)
      + radars.reduce((sum, r) => sum + (r.unread || 0), 0);
    onUnseenChange(total);
  }, [auto, radars, onUnseenChange]);

  // Load career status + CV presence once (gates the "Intro me" action).
  useEffect(() => {
    if (!user || didLoadProfile.current) return;
    didLoadProfile.current = true;
    (async () => {
      try {
        const resp = await apiCall(`${API_BASE}/api/v1/profiles/${user.userId}`);
        if (resp.ok) {
          const p = await resp.json();
          setCareerStatus(p.career_status || 'invisible');
          setHasCv(!!p.cv_id);
          setHasJobPrefs(profileHasJobPrefs(p.role_preferences, p.preferred_locations));
        }
      } catch { /* not critical */ }
    })();
  }, [user, apiCall]);

  // Greeting/nudge depend on hasJobPrefs, which loads from the SEPARATE profile
  // call above and can resolve AFTER the feed already rendered. Re-merge once it
  // changes so the greeting settles on the real (prefs) copy instead of being
  // stuck on the "nur CV + Tipp" fallback that was built before prefs arrived.
  const hasJobPrefsHydrated = useRef(false);
  useEffect(() => {
    if (!hasJobPrefsHydrated.current) { hasJobPrefsHydrated.current = true; return; }
    setChatLog(prev => remountFeedInChat(
      prev.filter(m => !isFeedMetaMsg(m)), items, selectedRef.current,
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasJobPrefs]);

  const updateCareerStatus = useCallback(async (newStatus: string): Promise<boolean> => {
    if (!user) return false;
    setSavingStatus(true);
    let ok = false;
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/profiles/${user.userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ career_status: newStatus }),
      });
      if (resp.ok) { setCareerStatus(newStatus); ok = true; }
    } catch { /* ignore */ }
    setSavingStatus(false);
    if (ok) setNudge(null);
    return ok;
  }, [user, apiCall]);

  const loadChatHistory = useCallback(async (sessionId: ThreadId): Promise<ChatMsg[]> => {
    try {
      const { messages } = await api.radarChatHistory(sessionId, 60);
      return messages.map((m, i) => ({
        id: `hist-${m.created_at}-${i}`,
        role: m.role === 'assistant' ? 'vee' : 'user',
        text: m.content,
        jobs: m.jobs && m.jobs.length > 0 ? m.jobs : undefined,
        createdAt: epochMs(m.created_at),
      }));
    } catch {
      return [];
    }
  }, [api]);

  const remountFeedInChat = useCallback((
    conversation: ChatMsg[],
    rows: FeedItem[],
    sel: ThreadId,
    radarSnap?: { auto: AutoRadar | null; radars: Radar[] },
  ) => {
    const autoRef = radarSnap?.auto ?? auto;
    const radarsRef = radarSnap?.radars ?? radars;
    const label = threadLabelFor(sel, autoRef, radarsRef, t);
    const pulled = pulledMoreIdsRef.current.get(sel) ?? [];
    return mergeFeedIntoChatLog(
      conversation,
      rows,
      feedMergeOpts(sel, rows, autoRef, radarsRef, label, hasJobPrefs, t, pulled),
    );
  }, [auto, radars, hasJobPrefs, t]);

  const loadFeed = useCallback(async (
    sel: ThreadId,
    conversation?: ChatMsg[],
    radarSnap?: { auto: AutoRadar | null; radars: Radar[] },
    opts?: { background?: boolean },
  ) => {
    const background = opts?.background ?? false;
    if (!background) {
      activeThreadLoadRef.current = sel;
      setFeedLoading(true);
    }
    setError(null);
    try {
      const feedOpts =
        sel === 'auto' ? { auto: true } :
        sel === 'interested' ? { status: 'interested' as const } :
        { watcherId: sel };
      const { items: rows } = await api.getFeed({ ...feedOpts, limit: FEED_API_LIMIT });
      if (activeThreadLoadRef.current !== sel) return rows;

      if (background) {
        setItems(rows);
        setChatLog(prev => {
          if (activeThreadLoadRef.current !== sel) return prev;
          const conv = prev.filter(m => !isFeedMetaMsg(m));
          return remountFeedInChat(conv, rows, sel, radarSnap);
        });
        return rows;
      }

      const hist = conversation ?? await loadChatHistory(sel);
      if (activeThreadLoadRef.current !== sel) return rows;
      setItems(rows);
      setChatLog(remountFeedInChat(hist, rows, sel, radarSnap));
      return rows;
    } catch (e: any) {
      if (activeThreadLoadRef.current !== sel) return [];
      if (!background) {
        setError(e?.message || t('error'));
        setItems([]);
      }
      return [];
    } finally {
      if (!background) setFeedLoading(false);
    }
  }, [api, t, loadChatHistory, remountFeedInChat]);

  const refreshFeed = useCallback(async (sel: ThreadId) => {
    if (selectedRef.current !== sel) return;
    try {
      const opts =
        sel === 'auto' ? { auto: true } :
        sel === 'interested' ? { status: 'interested' as const } :
        { watcherId: sel };
      const { items: rows } = await api.getFeed({ ...opts, limit: FEED_API_LIMIT });
      if (selectedRef.current !== sel) return;
      setItems(rows);
      setChatLog(prev => {
        if (selectedRef.current !== sel) return prev;
        return remountFeedInChat(prev.filter(m => !isFeedMetaMsg(m)), rows, sel);
      });
    } catch { /* ignore poll errors */ }
  }, [api, remountFeedInChat]);

  // Initial load — once signed in (skip auth-less calls when logged out).
  useEffect(() => {
    if (!isSignedIn) { setLoading(false); return; }
    let cancelled = false;
    pendingScrollBottomRef.current = true;
    (async () => {
      setLoading(true);
      try {
        const r = await loadRadars();
        if (cancelled) return;
        await loadFeed('auto', undefined, { auto: r.auto, radars: r.radars || [] });
        if (cancelled) return;
        // 'auto' is the default open tab — mark it seen on landing so its badge
        // doesn't linger while the user is already looking at the matches.
        try { await api.markRadarSeen('auto'); clearLocalUnread('auto'); } catch { /* ignore */ }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isSignedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live pool stats (refreshed occasionally). Loaded for guests too via the
  // public counts endpoint so the logged-out preview shows a real ticker.
  useEffect(() => {
    if (authLoading) return undefined;
    let alive = true;
    const load = () =>
      (isSignedIn ? api.marketPulse() : api.marketPulsePublic())
        .then(p => { if (alive) setPulse(p); })
        .catch(() => {});
    load();
    const id = setInterval(load, 120000);
    return () => { alive = false; clearInterval(id); };
  }, [api, isSignedIn, authLoading]);

  // Poll for new matches on the active thread (proactive feel).
  useEffect(() => {
    if (!isSignedIn) return undefined;
    const id = setInterval(async () => {
      await loadRadars();
      await refreshFeed(selected);
      // Keep the OPEN tab's badge clear: the reads above re-promote today's drop
      // (surfaced_at=now), which would otherwise re-mark the active radar unread
      // even though the user is sitting on it. New matches still show up as the
      // in-chat "neue Treffer" bubble — only the redundant tab badge is cleared.
      if (selected !== 'interested') {
        try { await api.markRadarSeen(selected); clearLocalUnread(selected); } catch { /* ignore */ }
      }
    }, 60000);
    return () => clearInterval(id);
  }, [selected, loadRadars, refreshFeed, isSignedIn, api, clearLocalUnread]);

  // Returning to the tab (window focus / page becomes visible): refresh the
  // active thread and snap the chat to the newest messages — same "land at the
  // bottom" feel as opening a thread, so a returning user always sees the latest.
  useEffect(() => {
    if (!isSignedIn) return undefined;
    const snapToBottom = () => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    };
    const onActive = () => {
      if (document.hidden) return;
      pendingScrollBottomRef.current = true;
      requestAnimationFrame(snapToBottom);
      Promise.resolve(refreshFeed(selectedRef.current)).finally(() => {
        pendingScrollBottomRef.current = true;
        requestAnimationFrame(snapToBottom);
      });
    };
    window.addEventListener('focus', onActive);
    document.addEventListener('visibilitychange', onActive);
    return () => {
      window.removeEventListener('focus', onActive);
      document.removeEventListener('visibilitychange', onActive);
    };
  }, [isSignedIn, refreshFeed]);

  // Fast poll while a brand-new radar is still filling (backfill runs async).
  useEffect(() => {
    if (!pendingRadarId || selected !== pendingRadarId) return undefined;
    let alive = true;
    let ticks = 0;
    const id = setInterval(() => {
      ticks += 1;
      if (!alive) return;
      loadRadars();
      refreshFeed(pendingRadarId);
      if (ticks >= 12) setPendingRadarId(null);
    }, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [pendingRadarId, selected, loadRadars, refreshFeed]);

  // Stop fast poll once jobs land on the pending radar.
  useEffect(() => {
    if (pendingRadarId && selected === pendingRadarId && items.length > 0) {
      setPendingRadarId(null);
    }
  }, [pendingRadarId, selected, items.length]);

  // Auto-scroll when new content arrives — but only if the user is already near
  // the bottom, or we explicitly want to follow (composer send). Card actions
  // (Intro me, dismiss) set suppressScrollRef so the view stays put.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Opening a thread: keep snapping to the bottom until real content has
    // rendered (the feed loads async, after the initial scroll-on-select), so we
    // always land on the newest matches instead of the pre-load top.
    if (pendingScrollBottomRef.current) {
      el.scrollTop = el.scrollHeight;
      if (chatLog.length > 0) pendingScrollBottomRef.current = false;
      return;
    }
    if (suppressScrollRef.current) {
      suppressScrollRef.current = false;
      return;
    }
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distFromBottom < 120) {
      el.scrollTop = el.scrollHeight;
    }
  }, [items, selected, chatLog, sending]);

  const selectThread = useCallback(async (sel: ThreadId) => {
    if (sel === selected) return;
    threadCacheRef.current.set(selected, { items, chatLog, expandedGroups });
    setVeeNote(null);
    setFeedCapReached(false);
    // Land at the newest matches once this thread's feed finishes loading.
    pendingScrollBottomRef.current = true;

    const cached = threadCacheRef.current.get(sel);
    activeThreadLoadRef.current = sel;
    selectedRef.current = sel;
    setSelected(sel);
    // Instant feel: drop this thread's unread the moment it's clicked (purely
    // local — the server markRadarSeen still fires AFTER the load below to dodge
    // the re-promote race). This is what makes the badge vanish on click both at
    // radar level and in the tab total (via onUnseenChange), no wait for load.
    if (sel !== 'interested') clearLocalUnread(sel);
    if (cached) {
      setItems(cached.items);
      setChatLog(cached.chatLog);
      setExpandedGroups(cached.expandedGroups);
      await loadFeed(sel, undefined, undefined, { background: true });
    } else {
      setItems([]);
      setChatLog([]);
      setExpandedGroups(new Set());
      await loadFeed(sel);
    }

    // Mark seen AFTER the feed load above (which promotes today's drop), then
    // clear the badge locally. Ordering matters: seen must be stamped once all
    // promotion for this open is done, and we must NOT loadRadars() afterwards
    // (it would re-promote past the seen stamp and re-light the badge).
    if (sel !== 'interested') {
      try { await api.markRadarSeen(sel); clearLocalUnread(sel); } catch { /* ignore */ }
    }
  }, [api, selected, items, chatLog, expandedGroups, loadFeed, clearLocalUnread]);

  // Keep thread cache warm while the user chats on the active tab.
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    if (loading) return;
    threadCacheRef.current.set(selected, { items, chatLog, expandedGroups });
  }, [loading, selected, items, chatLog, expandedGroups]);

  // Each radar thread opens scrolled to the latest messages.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [selected]);

  // Route async replies to the thread they belong to. If the user switched tabs
  // while a request was in flight, append to that thread's cache instead of the
  // currently-visible one so messages never land in the wrong chat.
  const appendToThread = useCallback((threadId: ThreadId, ...msgs: ChatMsg[]) => {
    if (selectedRef.current === threadId) {
      setChatLog(prev => [...prev, ...msgs]);
      return;
    }
    const snap = threadCacheRef.current.get(threadId);
    threadCacheRef.current.set(threadId, {
      items: snap?.items ?? [],
      expandedGroups: snap?.expandedGroups ?? new Set(),
      chatLog: [...(snap?.chatLog ?? []), ...msgs],
    });
  }, []);

  // Write the interest + flip the card to "Intro active". Only called once the
  // user is eligible (has a CV/passport AND a visible profile).
  const recordInterest = useCallback(async (item: FeedItem) => {
    const nextItems = items.map(i => (i.id === item.id ? { ...i, status: 'interested' as const } : i));
    setVeeNote(t('vee.interestedDone', { company: item.company_name }));
    setItems(nextItems);
    setChatLog(prev => remountFeedInChat(prev.filter(m => !isFeedMetaMsg(m)), nextItems, selected));
    try { await api.feedAction(item.id, 'interested'); } catch { await loadFeed(selected); }
    loadRadars();
  }, [items, selected, api, t, remountFeedInChat, loadFeed, loadRadars]);

  const act = async (item: FeedItem, action: 'dismiss' | 'interested' | 'uninterested') => {
    suppressScrollRef.current = true;
    // "Intro me": gate first so the active state never lies. It only counts as
    // active once there's a CV (passport) AND a visible profile.
    if (action === 'interested') {
      if (hasCv === false) { setCvReason('intro'); setNudge('cv'); return; }
      if (careerStatus === 'invisible') { setPendingIntroItem(item); setNudge('status'); return; }
      await recordInterest(item);
      return;
    }
    let nextItems: FeedItem[];
    if (action === 'dismiss') {
      nextItems = items.filter(i => i.id !== item.id);
      setVeeNote(t('vee.dismissed'));
    } else {
      nextItems = selected === 'interested'
        ? items.filter(i => i.id !== item.id)
        : items.map(i => (i.id === item.id ? { ...i, status: 'new' } : i));
    }
    setItems(nextItems);
    setChatLog(prev => remountFeedInChat(prev.filter(m => !isFeedMetaMsg(m)), nextItems, selected));
    try { await api.feedAction(item.id, action); } catch { await loadFeed(selected); }
    loadRadars();
  };

  const sendChat = async (text: string, opts?: { followScroll?: boolean }) => {
    const msg = text.trim();
    if (msg.length < 1 || sending) return;
    if (opts?.followScroll !== true) {
      suppressScrollRef.current = true;
    }
    setError(null);
    setComposer('');
    setVeeNote(null);

    // The thread this turn belongs to — guards against the user switching tabs
    // mid-request, which would otherwise drop the reply into the wrong chat.
    const targetThread = selected;
    const userMsg: ChatMsg = { id: nextMsgId(), role: 'user', text: msg, createdAt: Date.now() };
    appendToThread(targetThread, userMsg);

    const seenBeforeSend = collectJobIds(chatLog);
    if (items.length > 0 && selected !== 'interested') {
      items.forEach(i => { if (i.job_id) seenBeforeSend.add(i.job_id); });
    }

    // The on-screen job order (grouped into the UI's batches, newest batch last)
    // so the agent resolves "der N. von oben" against what the user actually sees.
    const visibleBatches = collectVisibleJobBatches(chatLog, expandedGroups);

    setSending(true);
    try {
      const res = await api.radarChat(msg, targetThread, visibleBatches);
      const freshJobs = dedupeAgentJobs(res.jobs || [], new Set(seenBeforeSend));
      const veeMsg: ChatMsg = {
        id: nextMsgId(),
        role: 'vee',
        text: res.response,
        jobs: freshJobs,
        createdAt: Date.now(),
      };
      if (res.radar_created && res.created_radar_id) {
        // Vee created a radar from this turn — navigate to it (deliberate) and
        // seed it with her reply. Sync the refs so follow-up loads target it.
        const tid = res.created_radar_id;
        const label = veeMsg.text?.trim() || msg;
        const optimistic: Radar = {
          id: tid,
          type: 'role',
          label,
          query: msg,
          filters: {},
          is_active: true,
          last_seen_at: null,
          created_at: Math.floor(Date.now() / 1000),
          total: 0,
          unread: 0,
          last_item_at: null,
        };
        setRadars(prev => [optimistic, ...prev.filter(r => r.id !== tid)]);
        selectedRef.current = tid;
        activeThreadLoadRef.current = tid;
        setSelected(tid);
        setPendingRadarId(tid);
        setVeeNote(null);
        setExpandedGroups(new Set());
        setFeedCapReached(false);
        const hist = await loadChatHistory(tid);
        await loadFeed(tid, hist.length > 0 ? hist : [veeMsg]);
        await loadRadars();
      } else {
        appendToThread(targetThread, veeMsg);
        if (res.interest_added) {
          await loadRadars();
          if (selectedRef.current === targetThread) await loadFeed(targetThread);
        }
      }
    } catch (e: any) {
      if (e?.status === 429 && e?.rateLimitInfo) {
        setRateLimitInfo(e.rateLimitInfo);
      } else {
        appendToThread(targetThread, { id: nextMsgId(), role: 'vee', text: t('error'), createdAt: Date.now() });
        setError(e?.message || t('error'));
      }
    } finally {
      setSending(false);
    }
  };

  // "Mehr aus dem Radar": the RELIABLE path. Force a fresh server pull for THIS
  // radar (same recall/filters as the daily drop — no narrow agent re-derivation
  // of role/location that strands the auto thread on the CV's home city) and
  // surface the next batch. The freshly-surfaced jobs render as a bottom "here
  // are more" burst (chat-native) and are kept out of the top seed, so pulling
  // more never re-grows the list at the top.
  const loadMoreFromRadar = async () => {
    if (loadingMore || sending || selected === 'interested') return;
    const target = selected;
    // "Before" = everything already on screen (seed cards + earlier bursts) AND
    // the current items state. Diffing only against `items` is fragile: if it's
    // momentarily stale/empty (tab switch, background refresh, race), every card
    // counts as "new", gets pulled to the bottom burst, and the original top
    // recommendations vanish — which reads as the chat history being wiped.
    const beforeIds = collectJobIds(chatLog);
    items.forEach(i => { if (i.job_id) beforeIds.add(i.job_id); });
    setLoadingMore(true);
    suppressScrollRef.current = false; // do follow to the new batch
    try {
      const opts = target === 'auto' ? { auto: true } : { watcherId: target };
      await api.loadMore(opts);
      const { items: rows } = await api.getFeed({ ...opts, limit: FEED_API_LIMIT });
      if (selectedRef.current !== target) return;
      const newIds = rows
        .map(r => r.job_id)
        .filter((id): id is string => !!id && !beforeIds.has(id));
      setItems(rows);
      if (newIds.length === 0) {
        setFeedCapReached(true);
        appendToThread(target, {
          id: nextMsgId(), role: 'vee',
          text: t('feedPrompt.pullMoreEmpty'), createdAt: Date.now(),
        });
        setChatLog(prev => remountFeedInChat(prev.filter(m => !isFeedMetaMsg(m)), rows, target));
      } else {
        // Record this pull as its OWN batch so it renders as a separate bubble
        // instead of merging into the previous "weitere Jobs" group.
        const batches = pulledMoreIdsRef.current.get(target) ?? [];
        batches.push(new Set(newIds));
        pulledMoreIdsRef.current.set(target, batches);
        savePulledMore(pulledMoreIdsRef.current);
        setChatLog(prev => remountFeedInChat(prev.filter(m => !isFeedMetaMsg(m)), rows, target));
      }
    } catch (e: any) {
      if (e?.status === 429 && e?.rateLimitInfo) {
        setRateLimitInfo(e.rateLimitInfo);
      } else {
        setError(e?.message || t('error'));
      }
    } finally {
      setLoadingMore(false);
    }
  };

  // "Ähnliche Jobs suchen": a reliable, fixed-size batch from the live pool
  // (NOT the free-form agent). Same idea as "more from radar" but broadens
  // beyond the radar's surfaced backlog: it semantic-searches the whole pool,
  // EXCLUDES everything already shown, and returns up to 5 genuinely-new cards —
  // so the count the user sees always matches the cards (the agent path used to
  // claim "8 found" then dedupe down to 1).
  const SIMILAR_BATCH = 5;
  const searchSimilarFromRadar = async () => {
    if (loadingMore || searchingSimilar || sending || selected === 'interested') return;
    const target = selected;
    const radar = target === 'auto' ? null : (radars.find(r => r.id === target) || null);

    // Everything already on screen (seed + bursts + chat + agent cards) is the
    // exclude set, and the shown titles seed the "similar to these" query.
    const seen = new Set<string>();
    const titles: string[] = [];
    const consider = (id?: string | null, title?: string | null) => {
      if (id) seen.add(id);
      if (title && titles.length < 3) titles.push(title);
    };
    for (const m of chatLog) {
      (m.feedItems || []).forEach(f => consider(f.job_id, f.title));
      (m.jobs || []).forEach(j => consider(j.job_id, j.title));
    }
    items.forEach(i => consider(i.job_id, i.title));

    const radarQuery = (radar?.query || radar?.label || '').trim();
    const location = (radar?.filters?.location as string | undefined)?.trim() || '';
    const remote = !!radar?.filters?.remote;
    const query = (radarQuery || titles.join(' / ') || t('feedPrompt.searchSimilar')).slice(0, 300);

    setSearchingSimilar(true);
    suppressScrollRef.current = false;
    try {
      const res = await api.searchJobs({
        query,
        location: location || undefined,
        remote,
        limit: SIMILAR_BATCH,
        exclude_job_ids: Array.from(seen).slice(0, 200),
      });
      if (selectedRef.current !== target) return;
      const fresh = dedupeAgentJobs(res.jobs || [], seen);
      const aiText = fresh.length > 0
        ? t('feedPrompt.searchSimilarResult', { count: fresh.length })
        : t('feedPrompt.searchSimilarEmpty');
      const userMsg: ChatMsg = {
        id: nextMsgId(), role: 'user',
        text: t('feedPrompt.searchSimilarMsg'), createdAt: Date.now(),
      };
      const veeMsg: ChatMsg = {
        id: nextMsgId(), role: 'vee', text: aiText, jobs: fresh, createdAt: Date.now() + 1,
      };
      appendToThread(target, userMsg, veeMsg);
      try { await api.persistChatTurn(target, userMsg.text || '', aiText, fresh); } catch { /* best-effort */ }
    } catch (e: any) {
      if (e?.status === 429 && e?.rateLimitInfo) {
        setRateLimitInfo(e.rateLimitInfo);
      } else {
        setError(e?.message || t('error'));
      }
    } finally {
      setSearchingSimilar(false);
    }
  };

  const submitComposer = () => { void sendChat(composer, { followScroll: true }); };

  // Agent-card "Intro me": send the request to Vee (she registers it via her tool),
  // and pop the same visibility/CV gate so the intro can actually reach companies.
  const introViaAgent = (text: string) => {
    void sendChat(text);
    if (hasCv === false) { setCvReason('intro'); setNudge('cv'); }
    else if (careerStatus === 'invisible') setNudge('status');
  };

  const dismissNudge = () => { setNudge(null); setPendingIntroItem(null); };

  // Picked a visible career status from the gate → go visible, then complete the
  // pending "Intro me" so the card flips to active only now.
  const goVisible = async (status: string) => {
    const ok = await updateCareerStatus(status);
    if (ok && pendingIntroItem) {
      const it = pendingIntroItem;
      setPendingIntroItem(null);
      await recordInterest(it);
    }
  };

  const handleVoiceToggle = async () => {
    setError(null);
    try {
      await voice.toggleRecording();
    } catch (e: unknown) {
      const code = e instanceof Error ? e.message : '';
      if (code === 'mic_denied' || code === 'audio_too_large') {
        setError(t('composer.micError'));
      } else {
        setError(t('error'));
      }
    }
  };

  const addRadar = async () => {
    const q = createInput.trim();
    if (q.length < 2 || creating) return;
    setCreating(true);
    try {
      const { watcher } = await api.createWatcher({ query: q });
      const radar = watcherToRadar(watcher);
      setRadars(prev => [radar, ...prev.filter(r => r.id !== radar.id)]);
      setCreateInput('');
      setCreateOpen(false);
      setSelected(watcher.id);
      setPendingRadarId(watcher.id);
      setVeeNote(null);
      setExpandedGroups(new Set());
      setFeedCapReached(false);
      await loadFeed(watcher.id);
      await loadRadars();
    } catch (e: any) {
      setError(e?.message || t('error'));
    } finally {
      setCreating(false);
    }
  };

  const removeRadar = async (id: string) => {
    threadCacheRef.current.delete(id);
    if (pulledMoreIdsRef.current.delete(id)) savePulledMore(pulledMoreIdsRef.current);
    setRadars(prev => prev.filter(r => r.id !== id));
    if (selected === id) {
      setSelected('auto');
      await loadFeed('auto');
    }
    try { await api.deleteWatcher(id); await loadRadars(); } catch { loadRadars(); }
  };

  const toggleAuto = async () => {
    if (enablingAuto) return;
    const next = !autoEnabled;
    // Gate BEFORE enabling: "Für dich" is CV-personalized — without a CV the
    // auto vector is empty and the feed stays blank, so nudge to upload first
    // instead of silently activating a dead radar. (Legacy WatchView enabled
    // first and nudged after; pre-gating is the honest UX.)
    if (next && hasCv === false) { setCvReason('auto'); setNudge('cv'); return; }
    if (next) {
      setEnablingAuto(true);
      setSelected('auto');
    }
    try {
      await api.setAuto(next);
      await loadRadars();
      await loadFeed('auto');
    } catch { /* ignore */ }
    finally { setEnablingAuto(false); }
  };

  const unreadTotal = useMemo(
    () => (auto?.unread || 0) + radars.reduce((s, r) => s + (r.unread || 0), 0),
    [auto, radars],
  );

  const conversationMsgs = useMemo(
    () => chatLog.filter(m => !isFeedMetaMsg(m)),
    [chatLog],
  );
  /** Hide feed action chips while the user is in a text turn (e.g. refine search). */
  const awaitingChatReply = useMemo(() => {
    for (let i = chatLog.length - 1; i >= 0; i--) {
      const m = chatLog[i];
      if (isFeedMetaMsg(m)) continue;
      if (m.role === 'user') return true;
      if (m.role === 'vee' && !(m.feedItems?.length) && !(m.jobs?.length)) return true;
      return false;
    }
    return false;
  }, [chatLog]);
  const showSetupLoader = enablingAuto
    || creating
    || (!!pendingRadarId && selected === pendingRadarId && items.length === 0);
  const setupLoaderTitle = enablingAuto
    ? t('empty.enablingForYouTitle')
    : t('create.creatingTitle');
  const showEmpty = !feedLoading && !loading
    && items.length === 0
    && conversationMsgs.length === 0
    && !(pendingRadarId && selected === pendingRadarId)
    && !creating
    && !enablingAuto;
  const showFeedFooter = selected !== 'interested' && items.length > 0 && !awaitingChatReply;
  const showSource = selected === 'all' || selected === 'interested';

  // Short, general nudge — the rotating radar examples live in the create-box
  // above, so the chat composer doesn't repeat them.
  const composerPlaceholder = useMemo(() => {
    if (selected === 'interested') return t('composer.placeholderInterested');
    return t('composer.placeholderDefault');
  }, [selected, t]);

  // Rotating example "nudges" shown as the create-box placeholder (like the
  // Community/Talks search): a diverse, shuffled list of radar examples cycles
  // through the empty input, and ✓ adopts the current one. Far more efficient on
  // mobile than a separate chip row — the hint lives inside the search field.
  const createNudges = useMemo<string[]>(() => {
    const raw = t('create.suggestions', { returnObjects: true }) as unknown;
    const all = Array.isArray(raw) ? (raw as string[]).filter(s => typeof s === 'string') : [];
    for (let i = all.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [all[i], all[j]] = [all[j], all[i]];
    }
    return all;
  }, [t]);
  const [nudgeIndex, setNudgeIndex] = useState(0);
  const [nudgeVisible, setNudgeVisible] = useState(true);
  useEffect(() => { setNudgeIndex(0); }, [createNudges]);
  useEffect(() => {
    if (createInput || createNudges.length <= 1) return;
    const timer = setInterval(() => {
      setNudgeVisible(false);
      setTimeout(() => {
        setNudgeIndex(i => (i + 1) % createNudges.length);
        setNudgeVisible(true);
      }, 300);
    }, 3800);
    return () => clearInterval(timer);
  }, [createInput, createNudges]);

  // Apply the user's saved drag order; unknown ids fall back to backend order.
  const orderedRadars = useMemo(() => {
    if (radarOrder.length === 0) return radars;
    const rank = new Map(radarOrder.map((id, i) => [id, i] as const));
    return [...radars].sort((a, b) => (rank.get(a.id) ?? 1e9) - (rank.get(b.id) ?? 1e9));
  }, [radars, radarOrder]);

  // Discovery: open the create bar once for users who have no radars at all
  // (auto off + no custom). Guarded so it never re-opens after they close it.
  useEffect(() => {
    if (createAutoOpened.current || loading) return;
    if (!autoEnabled && orderedRadars.length === 0) {
      setCreateOpen(true);
      createAutoOpened.current = true;
    } else if (autoEnabled || orderedRadars.length > 0) {
      createAutoOpened.current = true;
    }
  }, [autoEnabled, orderedRadars.length, loading]);

  const handleRadarReorder = useCallback((fromId: string, toId: string) => {
    if (fromId === toId) return;
    const ids = orderedRadars.map(r => r.id);
    const from = ids.indexOf(fromId);
    const to = ids.indexOf(toId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setRadarOrder(ids);
    try { localStorage.setItem(RADAR_ORDER_KEY, JSON.stringify(ids)); } catch { /* ignore */ }
  }, [orderedRadars]);

  // Logged-out: render the real Radar shell as a preview — header with the LIVE
  // public pool stats ("X companies · Y jobs · multiple sources") + a skeleton
  // conversation behind a sign-in prompt. No authed requests fire (only the
  // public market-pulse counts load); every action opens the auth modal.
  if (!isSignedIn && !authLoading) {
    return (
      <div
        ref={rootRef}
        style={viewHeight ? { height: viewHeight } : undefined}
        className="h-[calc(100dvh-92px)] sm:h-[calc(100vh-130px)] flex flex-col bg-gradient-to-br from-slate-50 via-white to-pink-50/40"
      >
        <div className="w-full max-w-3xl mx-auto px-3 sm:px-4 pt-2 sm:pt-4 flex flex-col h-full min-h-0">
          {/* Header with live pool stats */}
          <div className="mb-2 sm:mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3">
            <h1 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900 leading-tight">
              {t('header.title')}
            </h1>
            <div className="flex items-center gap-1.5 sm:flex-shrink-0">
              <RadarAnimation size={20} className="flex-shrink-0" />
              <LiveRadarStrip pulse={pulse} showPulseDot={false} showIcons={false} className="!mt-0" />
            </div>
          </div>

          {/* Chat-box shell with skeleton thread tabs + a faded skeleton convo */}
          <div className="flex-1 min-h-0 flex flex-col rounded-2xl border border-slate-200/80 bg-white/60 backdrop-blur-sm shadow-sm overflow-hidden">
            <div className="border-b border-slate-200/70 flex-shrink-0 flex items-stretch">
              <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3.5 py-2 sm:py-2.5 text-[11px] sm:text-xs font-semibold text-pink-600">
                <RadarIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />{t('threads.forYou')}
              </span>
              <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3.5 py-2 sm:py-2.5 text-[11px] sm:text-xs font-semibold text-slate-400">
                <Handshake className="w-3 h-3 sm:w-3.5 sm:h-3.5" />{t('threads.interested')}
              </span>
            </div>

            <div className="relative flex-1 min-h-0 overflow-hidden p-3 sm:p-4 space-y-3">
              <div className="flex items-start gap-2" aria-hidden>
                <VeeAvatar />
                <div className="rounded-2xl rounded-tl-sm bg-white border border-pink-100/90 px-3.5 py-3 shadow-sm w-64 max-w-[85%]">
                  <div className="h-2.5 w-40 max-w-full rounded bg-slate-200/80 animate-pulse mb-2" />
                  <div className="h-2.5 w-52 max-w-full rounded bg-slate-100 animate-pulse" />
                </div>
              </div>
              {[0, 1, 2].map(i => (
                <div key={i} className="ml-9 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm" aria-hidden>
                  <div className="h-2 w-24 rounded bg-slate-200/80 animate-pulse mb-2" />
                  <div className="h-3 w-44 max-w-full rounded bg-slate-100 animate-pulse mb-2" />
                  <div className="h-2 w-20 rounded bg-slate-100 animate-pulse" />
                </div>
              ))}

              {/* Sign-in prompt over the faded skeleton */}
              <div className="absolute inset-x-0 bottom-0 top-1/4 flex items-end justify-center bg-gradient-to-b from-transparent via-white/80 to-white px-4 pb-5">
                <div className="text-center max-w-xs">
                  <div
                    className="mx-auto mb-3 w-12 h-12 rounded-2xl grid place-items-center"
                    style={{ backgroundImage: CEVEE_AVATAR_GRADIENT }}
                  >
                    <Bot className="w-6 h-6 text-white" strokeWidth={2.25} />
                  </div>
                  <h2 className="text-base font-bold text-slate-900">{t('signedOut.title')}</h2>
                  <p className="text-[13px] text-slate-500 mt-1 leading-relaxed">{t('signedOut.body')}</p>
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="mt-3.5 w-full py-2.5 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all"
                  >
                    {t('signedOut.cta')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Composer placeholder — opens the auth modal instead of sending */}
          <div className="pt-2 pb-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowAuthModal(true)}
              className="w-full flex items-center gap-1.5 rounded-xl border border-pink-200/60 bg-white pl-3 pr-1.5 py-2 shadow-sm text-left hover:border-pink-300/80 transition-colors"
            >
              <span className="flex-1 min-w-0 truncate text-sm text-slate-400">{t('composer.placeholderDefault')}</span>
              <span
                className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-xl text-white shadow-sm"
                style={CEVEE_BTN_STYLE}
              >
                <Send className="w-3.5 h-3.5" />
              </span>
            </button>
          </div>
        </div>
        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} signupPath="/signup" />
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      style={viewHeight ? { height: viewHeight } : undefined}
      className="h-[calc(100dvh-92px)] sm:h-[calc(100vh-130px)] flex flex-col bg-gradient-to-br from-slate-50 via-white to-pink-50/40"
    >
      <div className="w-full max-w-3xl mx-auto px-3 sm:px-4 pt-2 sm:pt-4 flex flex-col h-full min-h-0">

        {/* Header: title (+ help) on the left, live stats with radar
            animation pushed to the right on one row. Stacks on mobile so
            the long stat line never squishes against the title. */}
        <div className="mb-2 sm:mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3">
          <div className="flex items-center gap-2">
            <h1 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900 leading-tight">
              {t('header.title')}
            </h1>
            <button
              type="button"
              onClick={() => setHowOpen(true)}
              title={t('how.title')}
              aria-label={t('how.title')}
              className="text-slate-400 hover:text-pink-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-300/80 focus-visible:ring-offset-1 rounded-full"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-1.5 sm:flex-shrink-0">
            <RadarAnimation size={20} className="flex-shrink-0" />
            <LiveRadarStrip
              pulse={pulse}
              unreadTotal={unreadTotal}
              showPulseDot={false}
              showIcons={false}
              className="!mt-0"
            />
          </div>
        </div>

        <AnimatePresence>
          {howOpen && (
            <HowItWorks onClose={() => setHowOpen(false)} pulse={pulse} unreadTotal={unreadTotal} />
          )}
        </AnimatePresence>

        {/* Collapsible radar-create bar above the chat. Collapsed = a compact
            "+ Radar" trigger (keeps the view clean once you already have radars);
            expanded = the full search field with rotating examples. */}
        {!createOpen ? (
          <div className="flex-shrink-0 mb-2">
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1 sm:gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 sm:px-3 py-1 sm:py-1.5 text-[11px] sm:text-xs font-semibold text-slate-500 shadow-sm hover:border-pink-200 hover:text-pink-600 transition-colors"
            >
              <Plus className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              {t('create.title')}
            </button>
          </div>
        ) : (
        <div className="flex-shrink-0 mb-2 rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center gap-2 p-2">
            <button
              type="button"
              onClick={() => { setCreateInput(''); setCreateOpen(false); }}
              title={t('create.cancel')}
              aria-label={t('create.cancel')}
              className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors ml-1"
            >
              <XIcon className="w-3.5 h-3.5" />
            </button>
            <div className="relative flex-1 min-w-0">
              <input
                value={createInput}
                onChange={e => setCreateInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addRadar(); if (e.key === 'Escape') { setCreateInput(''); setCreateOpen(false); } }}
                placeholder=""
                autoFocus
                className="w-full bg-transparent text-sm text-slate-800 focus:outline-none pr-7"
              />
              {/* Rotating example placeholder (Community/Talks-style): the hint
                  lives inside the field; ✓ adopts the current example. */}
              {!createInput && createNudges.length > 0 && (
                <>
                  <span
                    className="absolute left-0 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none truncate transition-opacity duration-300"
                    style={{ opacity: nudgeVisible ? 1 : 0, maxWidth: 'calc(100% - 1.75rem)' }}
                  >
                    {createNudges[nudgeIndex]}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCreateInput(createNudges[nudgeIndex])}
                    title={t('create.tryLabel')}
                    aria-label={t('create.tryLabel')}
                    className="absolute right-0 top-1/2 -translate-y-1/2 p-1 rounded-lg text-pink-400 hover:bg-pink-50 hover:text-pink-500 active:bg-pink-100 transition-colors"
                    style={{ opacity: nudgeVisible ? 1 : 0 }}
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
            <button
              onClick={addRadar}
              disabled={creating || createInput.trim().length < 2}
              className={`inline-flex items-center gap-1 rounded-full ${CEVEE_GRADIENT} px-2.5 sm:px-3 py-1 sm:py-1.5 text-[11px] sm:text-xs font-semibold text-white disabled:opacity-50 flex-shrink-0`}
            >
              {creating ? <Loader2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 animate-spin" /> : <Plus className="w-3 h-3 sm:w-3.5 sm:h-3.5" />}
              <span className="hidden sm:inline">{t('create.chip')}</span>
            </button>
          </div>
        </div>
        )}

        {/* Chat box: integrated thread tab strip + scrollable conversation */}
        <div className="flex-1 min-h-0 flex flex-col rounded-2xl border border-slate-200/80 bg-white/60 backdrop-blur-sm shadow-sm overflow-hidden">

          {/* Thread tabs — integrated in the box; scroll horizontally, radars drag-to-reorder */}
          <div className="border-b border-slate-200/70 flex-shrink-0">
            <LayoutGroup id="jobRadarThreads">
            <div className="flex items-stretch overflow-x-auto no-scrollbar">
              <WatchTab
                label={t('threads.forYou')}
                icon={<RadarIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />}
                active={selected === 'auto'}
                unread={auto?.unread || 0}
                onClick={() => selectThread('auto')}
              />
              <WatchTab
                label={t('threads.interested')}
                icon={<Handshake className="w-3 h-3 sm:w-3.5 sm:h-3.5" />}
                active={selected === 'interested'}
                onClick={() => selectThread('interested')}
              />
              {orderedRadars.map(r => (
                <WatchTab
                  key={r.id}
                  label={radarLabel(r)}
                  icon={<RadarIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />}
                  active={selected === r.id}
                  unread={r.unread || 0}
                  onClick={() => selectThread(r.id)}
                  draggable
                  dragging={dragId === r.id}
                  onDragStart={() => setDragId(r.id)}
                  onDragOver={e => { e.preventDefault(); if (dragId && dragId !== r.id) handleRadarReorder(dragId, r.id); }}
                  onDragEnd={() => setDragId(null)}
                  onDelete={() => setConfirmDeleteId(r.id)}
                  deleteLabel={t('actions.deleteRadar')}
                />
              ))}
            </div>
            </LayoutGroup>
          </div>

          {/* Delete-radar confirm bar (inside the box, under the tabs) */}
          <AnimatePresence initial={false}>
            {confirmDeleteId && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden flex-shrink-0 border-b border-slate-100"
              >
                <div className="m-2 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50/60 p-2">
                  <Trash2 className="w-4 h-4 text-red-500 flex-shrink-0 ml-1" />
                  <span className="flex-1 min-w-0 truncate text-sm text-slate-700">
                    {t('actions.deleteConfirm', {
                      label: radarLabel(radars.find(r => r.id === confirmDeleteId) || {} as Radar),
                    })}
                  </span>
                  <button
                    onClick={() => setConfirmDeleteId(null)}
                    className="rounded-full px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100 transition-colors"
                  >
                    {t('create.cancel')}
                  </button>
                  <button
                    onClick={() => { const id = confirmDeleteId; setConfirmDeleteId(null); if (id) removeRadar(id); }}
                    className="inline-flex items-center gap-1 rounded-full bg-red-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-600 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> {t('actions.confirmDelete')}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Scrollable conversation */}
          <div
            ref={scrollRef}
            className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4"
          >
          {loading ? (
            <ScanLoader title={t('header.scanning')} />
          ) : showSetupLoader ? (
            <ThreadSetupLoader title={setupLoaderTitle} />
          ) : (
            <AnimatePresence initial={false}>
              <motion.div
                key={selected}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.12, ease: 'easeOut' }}
                className="space-y-3 sm:space-y-4 relative"
              >
                {feedLoading && chatLog.length === 0 && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/70 backdrop-blur-[1px]">
                    <ScanLoader compact />
                  </div>
                )}

                {showEmpty && (
                  <EmptyThread
                    selected={selected}
                    autoEnabled={autoEnabled}
                    enablingAuto={enablingAuto}
                    onEnableAuto={toggleAuto}
                  />
                )}

                {veeNote && (
                  <VeeBubble>
                    <p>{veeNote}</p>
                  </VeeBubble>
                )}

                {chatLog.map((m, i) => {
                  const ts = m.createdAt;
                  const prevTs = chatLog[i - 1]?.createdAt;
                  const showDay = i === 0 || !sameCalendarDay(prevTs ?? ts, ts);
                  const shouldAnimate = !animatedMsgIdsRef.current.has(m.id);
                  if (shouldAnimate) animatedMsgIdsRef.current.add(m.id);
                  return (
                    <React.Fragment key={m.id}>
                      {showDay && <DayDivider label={formatDayLabel(ts, locale, t)} />}
                      {m.role === 'user' ? (
                        <UserBubble text={m.text || ''} animate={shouldAnimate} />
                      ) : (() => {
                        const groupFeed = m.feedItems || [];
                        const groupJobs = m.jobs || [];
                        const groupTotal = groupFeed.length + groupJobs.length;
                        const expanded = expandedGroups.has(m.id);
                        const shownFeed = expanded ? groupFeed : groupFeed.slice(0, FEED_PREVIEW);
                        const remainingJobsSlots = Math.max(0, FEED_PREVIEW - shownFeed.length);
                        const shownJobs = expanded ? groupJobs : groupJobs.slice(0, remainingJobsSlots);
                        const hiddenCount = groupTotal - (shownFeed.length + shownJobs.length);
                        return (
                          <div className="space-y-2">
                            <VeeBubble animate={shouldAnimate}>
                              <SafeMarkdown className="text-sm leading-relaxed [&_p]:mb-1.5">{m.text || ''}</SafeMarkdown>
                            </VeeBubble>
                            {shownFeed.map(item => (
                              <JobCard
                                key={item.id}
                                item={item}
                                locale={locale}
                                showSource={showSource}
                                visible={careerStatus !== 'invisible'}
                                onAction={act}
                              />
                            ))}
                            {shownJobs.length > 0 && (
                              <div className={`${JOB_CARD_STREAM} space-y-2`}>
                                {shownJobs.map(j => (
                                  <AgentJobCardView key={j.job_id} job={j} onInterest={introViaAgent} />
                                ))}
                              </div>
                            )}
                            {(hiddenCount > 0 || (expanded && groupTotal > FEED_PREVIEW)) && (
                              <button
                                type="button"
                                onClick={() => toggleGroup(m.id)}
                                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:border-pink-200 hover:bg-pink-50 hover:text-pink-700 transition-colors"
                              >
                                {hiddenCount > 0
                                  ? t('feedPrompt.showAll', { count: groupTotal })
                                  : t('feedPrompt.showLess')}
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </React.Fragment>
                  );
                })}

                {showFeedFooter && (
                  <FeedVeeFooter
                    selected={selected}
                    compact={conversationMsgs.length > 0}
                    loadingMore={loadingMore}
                    searchingSimilar={searchingSimilar}
                    capReached={feedCapReached}
                    onLoadMore={() => { void loadMoreFromRadar(); }}
                    onSimilar={() => { void searchSimilarFromRadar(); }}
                    onSuggest={sendChat}
                  />
                )}

                {sending && (
                  <div className="flex items-start gap-2">
                    <VeeAvatar />
                    <div className="rounded-2xl rounded-tl-sm bg-white border border-pink-100/90 px-3.5 py-3 shadow-sm">
                      <TypingDots />
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          )}
          </div>
        </div>

        {rateLimitInfo && (
          <RateLimitNotification
            rateLimitInfo={rateLimitInfo}
            feature="vee_chat"
            onClose={() => setRateLimitInfo(null)}
          />
        )}
        {error && !rateLimitInfo && (
          <div className="mt-2 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">
            {error}
          </div>
        )}

        {/* Composer */}
        <div className="pt-2 pb-1 flex-shrink-0">
          <div className="flex items-center gap-1.5 rounded-xl border border-pink-200/60 bg-white pl-3 pr-1.5 py-1 shadow-sm focus-within:ring-2 focus-within:ring-pink-300/50 focus-within:border-pink-300/80">
            <input
              value={composer}
              onChange={e => setComposer(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submitComposer(); }}
              onFocus={() => {
                // iOS resizes/offsets the visual viewport ~after the keyboard
                // animation, so recompute a few times to settle the composer
                // right above the keyboard and keep the latest messages in view.
                const settle = () => {
                  recomputeViewHeight();
                  const el = scrollRef.current;
                  if (el) el.scrollTop = el.scrollHeight;
                };
                settle();
                setTimeout(settle, 150);
                setTimeout(settle, 350);
              }}
              placeholder={composerPlaceholder}
              disabled={sending || voice.isTranscribing}
              className="flex-1 min-w-0 bg-transparent text-sm leading-normal focus:outline-none py-1 disabled:opacity-60"
            />
            <button
              type="button"
              onClick={() => void handleVoiceToggle()}
              disabled={sending || voice.isTranscribing}
              title={voice.isRecording ? t('composer.voiceStop') : t('composer.voiceStart')}
              aria-label={voice.isRecording ? t('composer.voiceStop') : t('composer.voiceStart')}
              className={`flex-shrink-0 inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-xl border transition-colors ${
                voice.isRecording
                  ? 'border-orange-300 bg-orange-50 text-orange-600 animate-pulse'
                  : 'border-pink-200/80 bg-white text-slate-600 hover:bg-pink-50 hover:text-pink-600'
              } disabled:opacity-40`}
            >
              {voice.isTranscribing
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Mic className="w-4 h-4" />}
            </button>
            <button
              onClick={submitComposer}
              disabled={sending || voice.isTranscribing || composer.trim().length < 1}
              className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-xl text-white shadow-sm disabled:opacity-40 transition-opacity hover:opacity-90"
              style={CEVEE_BTN_STYLE}
              aria-label={t('composer.send')}
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </button>
          </div>
          {(voice.isRecording || voice.isTranscribing) && (
            <p className="text-[11px] text-center mt-1 text-orange-600">
              {voice.isTranscribing
                ? t('composer.transcribing')
                : `${t('composer.voiceStop')} · ${voice.formatRecordingTime(voice.recordingTime)}`}
            </p>
          )}
          <p className="text-[0.5rem] sm:text-[0.65rem] text-slate-300 sm:text-slate-400 text-center leading-tight mt-0.5 sm:mt-1 px-1">
            {tc('veeTalks.disclaimer')}
          </p>
        </div>
      </div>

      {/* "Intro me" gate: CV / visibility nudges */}
      <AnimatePresence>
        {nudge && (
          <motion.div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={dismissNudge}
          >
            <motion.div
              className="w-full max-w-sm rounded-3xl bg-white p-6 text-center shadow-2xl"
              initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              {nudge === 'cv' ? (
                <>
                  <div className="mx-auto mb-3 w-12 h-12 rounded-2xl bg-pink-50 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-pink-500" />
                  </div>
                  <p className="font-bold text-slate-800">{t('nudge.cvTitle')}</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {cvReason === 'auto' ? t('nudge.cvBodyAuto') : t('nudge.cvBody')}
                  </p>
                  <button
                    onClick={() => { dismissNudge(); window.location.href = '/?tab=inspire'; }}
                    className="mt-4 w-full py-2.5 rounded-xl bg-gradient-to-r from-pink-500 to-orange-400 text-white font-semibold text-sm"
                  >
                    {t('nudge.cvCta')}
                  </button>
                  <button onClick={dismissNudge} className="mt-2 text-sm text-slate-400 hover:text-slate-600">
                    {t('nudge.later')}
                  </button>
                </>
              ) : (
                <>
                  <div className="mx-auto mb-3 w-12 h-12 rounded-2xl bg-pink-50 flex items-center justify-center">
                    <Eye className="w-6 h-6 text-pink-500" />
                  </div>
                  <p className="font-bold text-slate-800">{t('nudge.statusTitle')}</p>
                  <p className="text-sm text-slate-500 mt-1">{t('nudge.statusBody')}</p>
                  <div className="mt-4 space-y-2">
                    <button
                      disabled={savingStatus}
                      onClick={() => goVisible('actively_seeking')}
                      className="w-full py-2.5 rounded-xl bg-gradient-to-r from-pink-500 to-orange-400 text-white font-semibold text-sm disabled:opacity-50"
                    >
                      {t('nudge.statusActive')}
                    </button>
                    <button
                      disabled={savingStatus}
                      onClick={() => goVisible('curious')}
                      className="w-full py-2.5 rounded-xl bg-slate-100 text-slate-700 font-semibold text-sm disabled:opacity-50"
                    >
                      {t('nudge.statusCurious')}
                    </button>
                  </div>
                  <button onClick={dismissNudge} className="mt-2 text-sm text-slate-400 hover:text-slate-600">
                    {t('nudge.later')}
                  </button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Chat stream: indent past Vee avatar; card width follows content (title), capped like bubbles.
// Chat stream: indent past Vee avatar; card width follows content (title), capped like bubbles.
const JOB_CARD_STREAM = 'pl-9 w-fit max-w-[85%]';

const VeeAvatar: React.FC<{ size?: 'sm' | 'lg' }> = ({ size = 'sm' }) => (
  <div
    className={`grid place-items-center flex-shrink-0 mt-0.5 rounded-xl shadow-sm overflow-hidden ${
      size === 'lg' ? 'w-12 h-12 rounded-2xl' : 'w-7 h-7'
    }`}
    style={{ backgroundImage: CEVEE_AVATAR_GRADIENT }}
    aria-hidden
  >
    <Bot
      className={`text-white block ${size === 'lg' ? 'w-6 h-6' : 'w-[17px] h-[17px]'}`}
      strokeWidth={2.25}
      style={{ transform: 'translateY(0.5px)' }}
    />
  </div>
);

// ── Unified scanning / loading state ─────────────────────────────────────────
// One cool look for every loading moment (initial scan, radar creation, "For
// you" setup, feed refresh): the Vee icon with soft radar ripples + one short
// line. No spinners, no typing dots — keeps it clean and consistent.
const ScanLoader: React.FC<{
  title?: string;
  body?: string;
  compact?: boolean;
}> = ({ title, body, compact = false }) => {
  if (compact) {
    return (
      <div className="relative grid place-items-center w-7 h-7" aria-hidden>
        <span className="absolute inset-0 rounded-xl bg-pink-400/30 animate-ping" />
        <div className="relative">
          <VeeAvatar />
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center text-center h-full min-h-[150px] py-6 sm:min-h-[220px] sm:py-10">
      <div className="relative grid place-items-center w-9 h-9 mb-2.5 sm:w-12 sm:h-12 sm:mb-4" aria-hidden>
        <span className="absolute inset-0 rounded-2xl bg-pink-400/30 animate-ping" />
        <span
          className="absolute inset-0 rounded-2xl bg-pink-400/20 animate-ping"
          style={{ animationDelay: '0.7s' }}
        />
        <div className="relative">
          <span className="sm:hidden"><VeeAvatar /></span>
          <span className="hidden sm:block"><VeeAvatar size="lg" /></span>
        </div>
      </div>
      {title && <p className="font-semibold text-slate-800 text-[13px] sm:text-base">{title}</p>}
      {body && <p className="text-[11px] sm:text-sm text-slate-500 max-w-[15rem] sm:max-w-xs mt-0.5 sm:mt-1 leading-relaxed">{body}</p>}
    </div>
  );
};

const DayDivider: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center gap-3 py-1">
    <div className="flex-1 h-px bg-slate-200/90" />
    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide shrink-0">
      {label}
    </span>
    <div className="flex-1 h-px bg-slate-200/90" />
  </div>
);

// ── Vee follow-up under feed preview (replaces "Load more" button) ───────────
const FooterChip: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:border-pink-200 hover:bg-pink-50 hover:text-pink-700 transition-colors disabled:opacity-50"
  >
    {children}
  </button>
);

const FeedVeeFooter: React.FC<{
  selected: ThreadId;
  compact: boolean;
  loadingMore: boolean;
  searchingSimilar: boolean;
  capReached: boolean;
  onLoadMore: () => void;
  onSimilar: () => void;
  onSuggest: (text: string) => void;
}> = ({
  selected, compact,
  loadingMore, searchingSimilar, capReached, onLoadMore, onSimilar, onSuggest,
}) => {
  const { t } = useTranslation('jobRadarAi');
  if (selected === 'interested') return null;

  // The count of "new" matches already lives in the seed greeting, each result
  // bubble's header, and the tab badge — repeating it here just confused (e.g.
  // "10 neue" right after the user pulled/expanded those very cards). So the
  // footer is action-only: a short "what next?" lead-in plus the chips.
  return (
    <VeeBubble>
      {compact && (
        <p className="text-slate-600">{t('feedPrompt.whatNext')}</p>
      )}
      <div className={`flex flex-wrap gap-1.5 ${compact ? 'mt-2' : ''}`}>
        {!capReached && (
          <FooterChip onClick={onLoadMore} disabled={loadingMore || searchingSimilar}>
            {loadingMore && <Loader2 className="w-3 h-3 animate-spin" />}
            {t('feedPrompt.pullMore')}
          </FooterChip>
        )}
        <FooterChip onClick={onSimilar} disabled={loadingMore || searchingSimilar}>
          {searchingSimilar && <Loader2 className="w-3 h-3 animate-spin" />}
          {t('feedPrompt.searchSimilar')}
        </FooterChip>
        <FooterChip onClick={() => onSuggest(t('feedPrompt.refineMsg'))}>
          {t('feedPrompt.refine')}
        </FooterChip>
      </div>
    </VeeBubble>
  );
};

// ── Integrated thread tab (Passport-style strip; radars are drag-to-reorder) ──
const WatchTab: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
  unread?: number;
  icon?: React.ReactNode;
  draggable?: boolean;
  dragging?: boolean;
  onDragStart?: () => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  onDelete?: () => void;
  deleteLabel?: string;
}> = ({
  label, active, onClick, unread = 0, icon,
  draggable, dragging, onDragStart, onDragOver, onDragEnd,
  onDelete, deleteLabel,
}) => (
  <div
    draggable={draggable}
    onDragStart={onDragStart}
    onDragOver={onDragOver}
    onDragEnd={onDragEnd}
    onDrop={onDragEnd}
    className={`relative flex-shrink-0 inline-flex items-stretch transition-colors ${
      dragging ? 'opacity-40' : ''
    } ${draggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
  >
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 sm:gap-1.5 py-2 sm:py-2.5 text-[11px] sm:text-xs font-semibold whitespace-nowrap transition-colors ${
        onDelete && active ? 'pl-2.5 sm:pl-3.5 pr-1' : 'px-2.5 sm:px-3.5'
      } ${active ? 'text-pink-600' : 'text-slate-400 hover:text-slate-600'}`}
    >
      {icon}
      <span className="max-w-[110px] sm:max-w-[150px] truncate">{label}</span>
      {unread > 0 && (
        <span className={`inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold ${
          active ? `${CEVEE_GRADIENT} text-white` : 'bg-slate-200 text-slate-600'
        }`}>
          {unread}
        </span>
      )}
    </button>
    {onDelete && active && (
      <button
        onClick={onDelete}
        title={deleteLabel}
        aria-label={deleteLabel}
        className="inline-flex items-center pr-2.5 pl-0.5 text-slate-300 hover:text-red-500 transition-colors"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    )}
    {active && (
      <motion.div
        layoutId="watchThreadTab"
        initial={false}
        transition={{ type: 'spring', stiffness: 520, damping: 34 }}
        className="absolute bottom-0 left-2 right-2 h-[2.5px] rounded-full bg-gradient-to-r from-pink-400 to-rose-500"
      />
    )}
  </div>
);

// ── Vee message bubble ───────────────────────────────────────────────────────
const ChatBubbleShell: React.FC<{
  animate?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ animate = true, className, children }) => {
  if (!animate) return <div className={className}>{children}</div>;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const VeeBubble: React.FC<{ children: React.ReactNode; animate?: boolean }> = ({ children, animate = true }) => (
  <ChatBubbleShell animate={animate} className="flex items-start gap-2">
    <VeeAvatar />
    <div className={`rounded-2xl rounded-tl-sm bg-white border border-pink-100/90 px-3 py-2 sm:px-3.5 sm:py-2.5 text-[13px] sm:text-sm text-slate-700 shadow-sm max-w-[85%]`}>
      {children}
    </div>
  </ChatBubbleShell>
);

// ── User message bubble ──────────────────────────────────────────────────────
const UserBubble: React.FC<{ text: string; animate?: boolean }> = ({ text, animate = true }) => (
  <ChatBubbleShell animate={animate} className="flex justify-end">
    <div className="rounded-2xl rounded-tr-sm bg-white border border-slate-200 text-slate-800 px-3 py-2 sm:px-3.5 sm:py-2.5 text-[13px] sm:text-sm shadow-sm max-w-[85%] whitespace-pre-wrap">
      {text}
    </div>
  </ChatBubbleShell>
);

// ── Typing indicator ─────────────────────────────────────────────────────────
const TypingDots: React.FC = () => (
  <div className="flex items-center gap-1">
    {[0, 1, 2].map(i => (
      <motion.span
        key={i}
        className="w-1.5 h-1.5 rounded-full bg-pink-500"
        animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
        transition={{ duration: 1, repeat: Infinity, delay: i * 0.2, ease: 'easeInOut' }}
      />
    ))}
  </div>
);

// ── Agent job card (from a chat tool result) ─────────────────────────────────
const AgentJobCardView: React.FC<{
  job: AgentJobCard;
  onInterest: (text: string) => void;
}> = ({ job, onInterest }) => {
  const { t } = useTranslation('jobRadarAi');
  const loc = job.location || (job.country === 'remote' ? t('card.remote') : null);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-2.5 py-2 sm:px-3.5 sm:py-3 shadow-sm">
      <div>
        <p className="text-[10px] sm:text-[11px] font-semibold text-slate-500">{job.company_name}</p>
        <p className="font-bold text-slate-900 text-[13px] sm:text-sm leading-snug">{job.title}</p>
        {loc && (
          <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] sm:text-[11px] text-slate-500">
            <MapPin className="w-3 h-3" /> {loc}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 mt-2 sm:mt-2.5">
        {job.company_name && (
          <button
            onClick={() => onInterest(`Stell mich bei ${job.company_name} vor (Intro me)`)}
            className="inline-flex items-center gap-1 sm:gap-1.5 rounded-lg bg-slate-50 text-slate-600 hover:bg-pink-50 hover:text-pink-600 px-2 py-1 sm:px-2.5 sm:py-1.5 text-[11px] sm:text-xs font-semibold transition-colors"
          >
            <Handshake className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> {t('card.interested')}
          </button>
        )}
        {job.apply_url && (
          <a
            href={job.apply_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 sm:gap-1.5 rounded-lg bg-slate-900 text-white px-2 py-1 sm:px-2.5 sm:py-1.5 text-[11px] sm:text-xs font-semibold hover:bg-slate-700 transition-colors"
          >
            <ExternalLink className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> {t('card.apply')}
          </a>
        )}
      </div>
    </div>
  );
};

// ── Thread setup loader (auto enable / new radar) ────────────────────────────
const ThreadSetupLoader: React.FC<{ title: string }> = ({ title }) => (
  <ScanLoader title={title} />
);

// ── Empty thread ─────────────────────────────────────────────────────────────
const EmptyThread: React.FC<{
  selected: ThreadId;
  autoEnabled: boolean;
  enablingAuto: boolean;
  onEnableAuto: () => void;
}> = ({ selected, autoEnabled, enablingAuto, onEnableAuto }) => {
  const { t } = useTranslation('jobRadarAi');
  const body = selected === 'auto'
    ? (autoEnabled ? t('empty.forYouOn') : t('empty.forYouOff'))
    : selected === 'interested'
      ? t('empty.interested')
      : t('empty.radar');
  // The "activate For you" screen isn't really an empty result — drop the avatar
  // and the "no matches yet" title there; just explain + offer the activate CTA.
  const isActivate = selected === 'auto' && !autoEnabled;
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      {!isActivate && (
        <>
          <div className="mb-3">
            <VeeAvatar size="lg" />
          </div>
          <p className="font-semibold text-slate-700">{t('empty.title')}</p>
        </>
      )}
      <p className="text-sm text-slate-500 max-w-sm mt-1">{body}</p>
      {selected === 'auto' && !autoEnabled && (
        <button
          onClick={onEnableAuto}
          disabled={enablingAuto}
          className={`mt-4 inline-flex items-center gap-1.5 rounded-xl ${CEVEE_GRADIENT} px-4 py-2 text-sm font-semibold text-white disabled:opacity-70`}
        >
          {enablingAuto
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Bot className="w-4 h-4" strokeWidth={2.25} />}
          {enablingAuto ? t('empty.enablingForYouTitle') : t('empty.enableForYou')}
        </button>
      )}
    </div>
  );
};

// ── Job card (rendered inside Vee's message stream) ──────────────────────────
const JobCard: React.FC<{
  item: FeedItem;
  locale: string;
  showSource: boolean;
  visible: boolean;
  onAction: (item: FeedItem, action: 'dismiss' | 'interested' | 'uninterested') => void;
}> = ({ item, locale, showSource, visible, onAction }) => {
  const { t } = useTranslation('jobRadarAi');
  // "Intro active" is only truthful when the profile is also visible — otherwise
  // the intro can't reach companies yet, so we keep it as a plain "Intro me".
  const introActive = item.status === 'interested' && visible;
  const early = item.employment_type ? EARLY_CAREER.has(item.employment_type) : false;
  const loc = item.location || (item.country === 'remote' ? t('card.remote') : null);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      className={JOB_CARD_STREAM}
    >
      <div className={`rounded-2xl border bg-white px-2.5 py-2 sm:px-3.5 sm:py-3 shadow-sm transition-colors ${
        early ? 'border-pink-200' : 'border-slate-200'
      }`}>
        <div>
          <p className="text-[10px] sm:text-[11px] font-semibold text-slate-500">{item.company_name}</p>
          <p className="font-bold text-slate-900 text-[13px] sm:text-sm leading-snug">{item.title}</p>
          <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-0.5 text-[10px] sm:text-[11px] text-slate-500">
              {loc && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {loc}
                </span>
              )}
              {early && (
                <span className="inline-flex items-center rounded-full bg-pink-50 text-pink-600 font-semibold px-1.5 py-0.5 text-[10px]">
                  {item.employment_type}
                </span>
              )}
              {showSource && <span className="text-slate-400">· {item.source}</span>}
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 mt-2 sm:mt-2.5">
          <button
            onClick={() => onAction(item, introActive ? 'uninterested' : 'interested')}
            title={introActive ? t('card.undoInterested') : t('card.interested')}
            className={`inline-flex items-center gap-1 sm:gap-1.5 rounded-lg px-2 py-1 sm:px-2.5 sm:py-1.5 text-[11px] sm:text-xs font-semibold transition-colors ${
              introActive
                ? 'bg-pink-100 text-pink-700'
                : 'bg-slate-50 text-slate-600 hover:bg-pink-50 hover:text-pink-600'
            }`}
          >
            <Handshake className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ${introActive ? 'fill-current' : ''}`} />
            {introActive ? t('card.interestedActive') : t('card.interested')}
          </button>
          {item.apply_url && (
            <a
              href={item.apply_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 sm:gap-1.5 rounded-lg bg-slate-900 text-white px-2 py-1 sm:px-2.5 sm:py-1.5 text-[11px] sm:text-xs font-semibold hover:bg-slate-700 transition-colors"
            >
              <ExternalLink className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> {t('card.apply')}
            </a>
          )}
          <button
            onClick={() => onAction(item, 'dismiss')}
            title={t('card.dismiss')}
            aria-label={t('card.dismiss')}
            className="ml-auto inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <XIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ── How it works (info icon) ─────────────────────────────────────────────────
const HowItWorks: React.FC<{
  onClose: () => void;
  pulse: MarketPulse | null;
  unreadTotal: number;
}> = ({ onClose, pulse, unreadTotal }) => {
  const { t } = useTranslation('jobRadarAi');
  const steps = [
    { icon: Bot, title: t('how.step1Title'), body: t('how.step1Body') },
    { icon: RadarIcon, title: t('how.step2Title'), body: t('how.step2Body') },
    { icon: Sparkles, title: t('how.step3Title'), body: t('how.step3Body') },
    { icon: Handshake, title: t('how.step4Title'), body: t('how.step4Body') },
  ];
  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl"
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 24, opacity: 0 }}
        onClick={e => e.stopPropagation()}
      >
        <p className="font-bold text-slate-800 text-lg mb-3">{t('how.title')}</p>
        <LiveRadarStrip
          pulse={pulse}
          unreadTotal={unreadTotal}
          showPulseDot={false}
          showScanningLabel
          className="mb-4 !mt-0"
        />
        <div className="space-y-4">
          {steps.map(({ icon: Icon, title, body }, i) => (
            <div key={i} className="flex gap-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-pink-50 flex items-center justify-center">
                <Icon className="w-4 h-4 text-pink-500" />
              </div>
              <div>
                <p className="font-semibold text-slate-800 text-sm">{title}</p>
                <p className="text-sm text-slate-500 leading-snug">{body}</p>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-5 w-full py-2.5 rounded-xl bg-slate-900 text-white font-semibold text-sm"
        >
          {t('how.close')}
        </button>
      </motion.div>
    </motion.div>
  );
};

export default JobRadarAIView;
